import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaHotel,
  FaBuilding,
  FaBagShopping,
  FaCartShopping,
  FaTruck,
  FaChartLine,
  FaRocket,
  FaUserDoctor,
  FaBuildingColumns
} from 'react-icons/fa6';

const Services: React.FC = () => {
  const location = useLocation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredValueProp, setHoveredValueProp] = useState<number | null>(null);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a temporary highlight effect
          element.classList.add('ring-4', 'ring-primary/20');
          setTimeout(() => {
            element.classList.remove('ring-4', 'ring-primary/20');
          }, 2000);
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const services = [
    {
      title: "Website Development",
      description: "Build scalable, secure, and high-performance web applications tailored to your specific business needs.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      color: "bg-[#3b82f6]", // Blue
      features: [
        "Custom Enterprise Web Apps",
        "Progressive Web Applications (PWA)",
        "API Integration & Development",
        "Legacy Application Modernization"
      ],
      link: "/services/web-development"
    },
    {
      title: "Mobile Application Development",
      description: "Create engaging and intuitive mobile experiences that reach your users on any iOS or Android device.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      color: "bg-[#10b981]", // Green
      features: [
        "Native iOS & Android Apps",
        "Cross-Platform React Native/Flutter",
        "Enterprise Mobility Solutions",
        "App Store Launch & Optimization"
      ],
      link: "/services/mobile-development"
    },
    {
      title: "Custom Software Development",
      description: "Bespoke software solutions built from the ground up to solve your unique business challenges and streamline workflows.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      color: "bg-[#8b5cf6]", // Purple
      features: [
        "End-to-End Product Development",
        "Scalable Cloud Architecture",
        "Microservices & Serverless",
        "System Architecture & Design"
      ],
      link: "/services/custom-software"
    },
    {
      title: "UI/UX Design",
      description: "Design user-centric interfaces that deliver exceptional and delightful digital experiences, balancing aesthetics with functionality.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: "bg-[#ef4444]", // Red
      features: [
        "User Research & Testing",
        "Wireframing & Prototyping",
        "High-Fidelity Visual Design",
        "Design Systems & Brand Identity"
      ],
      link: "/services/ui-ux-design"
    },
    {
      title: "Specialized AI Agent Swarms",
      description: "We deploy autonomous AI agents tailored for specific business functions, working together to handle complex, multi-step workflows.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      color: "bg-[#f59e0b]", // Amber
      features: [
        "Sales Agents: Automated lead scraping, cold outreach & appointment setting.",
        "HR Agents: Talent sourcing, resume parsing & automated screening.",
        "Finance Agents: Reconciliation, invoice processing & payment tracking.",
        "Support Agents: Case resolution, knowledge base extraction & escalation."
      ],
      link: "/autonomous-agents"
    },
    {
      title: "AI for E-commerce Scale",
      description: "End-to-end AI integration for online retailers to maximize conversion and operational efficiency.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      color: "bg-[#fbbf24]", // Yellow
      features: [
        "Cart Recovery AI: Autonomous re-engagement of high-intent buyers.",
        "Personalized Recommendation: 1:1 product matching at scale.",
        "Dynamic Pricing: Real-time competitive price adjustment.",
        "Visual Search: Allow customers to search with photos & screenshots."
      ]
    },
    {
      title: "Data & Predictive Analytics",
      description: "Turn raw data into strategic assets with advanced predictive modeling.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: "bg-[#f43f5e]", // Rose
      features: [
        "Customer Segmentation & LTV",
        "Sales & Demand Forecasting",
        "Churn Prediction Strategies",
        "Retention Modeling"
      ]
    },
    {
      id: "ai-operations",
      title: "AI Automation for Operations",
      description: "Streamline business processes and reduce operational costs with intelligent automation.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
        </svg>
      ),
      color: "bg-[#0ea5e9]", // Sky
      features: [
        "Robotic Process Automation (RPA)",
        "Document & Invoice Processing",
        "AI HR & Recruitment Tools",
        "Workflow Optimization"
      ]
    },
    {
      title: "AI Creative Services",
      description: "Production-grade visuals and content created at the speed of AI for standout branding.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      color: "bg-[#a855f7]", // Purple
      features: [
        "AI-Generated Branding Visuals",
        "AI Video Editing & Reels",
        "Multilingual Voiceovers",
        "Avatar-based Product Demos"
      ]
    },
    {
      title: "AI for Hospitality & Whitelabel",
      description: "Elevate guest experience with custom-built AI concierge and whitelabel streaming solutions for resorts.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: "bg-[#34d399]", // Emerald
      features: [
        "AI Concierge: 24/7 multilingual guest support via WhatsApp.",
        "Whitelabel Streaming: Secure, in-room video & resource portals for luxury hotels.",
        "Dynamic Booking: AI-driven upselling and yield management.",
        "Sentiment Analysis: Real-time monitoring of guest reviews & feedback."
      ]
    },
    {
      title: "AI Solutions for Real Estate",
      description: "Modernize property buying and selling with predictive intelligence and virtual experiences.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      color: "bg-[#06b6d4]", // Cyan
      features: [
        "AI Property Valuation API",
        "Predictive Buyer Matching",
        "AI-Enhanced Virtual Tours",
        "Lead Scoring & Prioritization"
      ]
    },
    {
      id: "ai-training",
      title: "AI Training & Consulting",
      description: "Empowering leadership and teams to understand and leverage AI effectively.",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      color: "bg-[#3b82f6]", // Blue
      features: [
        "Executive AI Workshops",
        "Strategic AI Roadmaps",
        "Implementation Support",
        "Proof-of-Concept Development"
      ]
    },
    {
      title: "AI Chatbots",
      description: "Website, WhatsApp, and customer support chatbots that engage customers 24/7",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      color: "bg-[#3b82f6]", // Primary Blue
      features: [
        "24/7 Customer Support",
        "WhatsApp & Web Integration",
        "Instant Lead Capture",
        "Multilingual Support"
      ],
      link: "/services/ai-chatbots"
    },
    {
      title: "Business Automation",
      description: "Automate invoices, leads, HR processes, emails, and social media",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: "bg-[#10b981]", // Secondary Green/Teal
      features: [
        "Invoice Processing",
        "Email & CRM Automation",
        "Social Media Auto-posting",
        "HR Onboarding Workflows"
      ],
      link: "/services/business-automation"
    },
    {
      title: "Document AI",
      description: "PDF extraction, resume screening, contract analysis, and data processing",
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: "bg-[#1e293b]", // Dark
      features: [
        "Smart PDF Extraction",
        "Resume Parsing & Screening",
        "Contract Data Analysis",
        "Automated Data Entry"
      ],
      link: "/services/document-ai"
    }
  ];

  return (
    <div className="bg-white">
      {/* Header Section */}
      {/* Header Section */}
      <section className="bg-primary pt-32 pb-20 lg:pt-40">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-blue-100 font-bold tracking-wider text-sm mb-4 uppercase">
            Global AI Solutions
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-inter">
            Transforming Enterprises in
            <span className="block text-secondary mt-2">India</span>
          </h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
            We deliver specialized AI strategies designed for the unique needs of the Indian market, from multilingual automation to smart city innovation.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const colorCode = service.color.match(/\[(.*?)\]/)?.[1] || '#3b82f6'; // Extract hex or default to blue

              return (
                <div
                  key={index}
                  id={service.id}
                  className="group relative overflow-hidden bg-white rounded-3xl p-10 border border-gray-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    backgroundImage: hoveredIndex === index
                      ? `linear-gradient(to bottom right, ${colorCode}0d, transparent)`
                      : undefined
                  }}
                >
                  <div className={`w-16 h-16 ${service.color} rounded-2xl flex items-center justify-center mb-8 transform group-hover:scale-110 transition-transform duration-300`}
                    style={{ boxShadow: `0 10px 15px -3px ${colorCode}33` }}>
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-dark mb-4 transition-colors duration-300"
                    style={{ color: hoveredIndex === index ? colorCode : undefined }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-500 mb-8 max-w-lg leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8 w-full">
                    {service.features.map((feature, i) => (
                      <li key={i} className="text-base text-dark font-medium">
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    {(service as any).link && (
                      <Link
                        to={(service as any).link}
                        className="inline-flex items-center font-bold transition-colors group text-lg"
                        style={{ color: hoveredIndex === index ? colorCode : '#64748b' }} // Default to gray/secondary or retain original style? Original was secondary.
                      >
                        Learn More
                        <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Target Industries Section */}
      <section className="py-20 bg-white border-t border-gray-50">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-secondary font-bold tracking-wider text-sm mb-4 uppercase">
            Target Industries
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
            Tailored for Key Sectors
          </h2>
          <p className="text-gray-500 mb-16 max-w-2xl mx-auto">
            We focus on high-growth industries where AI impact is most transformative.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 gap-x-8">
            {[
              { name: "Hospitality & Hotels", icon: FaHotel },
              { name: "Real Estate Developers", icon: FaBuilding },
              { name: "Retail & Luxury Brands", icon: FaBagShopping },
              { name: "E-commerce Platforms", icon: FaCartShopping },
              { name: "Logistics & Supply Chain", icon: FaTruck },
              { name: "Financial Services / Fintech", icon: FaChartLine },
              { name: "Startups & Tech Hubs", icon: FaRocket },
              { name: "Healthcare Providers", icon: FaUserDoctor },
              { name: "Government / Smart City", icon: FaBuildingColumns }
            ].map((industry, index) => (
              <div key={index} className="flex flex-col items-center group">
                <div className="w-24 h-24 bg-white border border-gray-100 rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-105">
                  {React.createElement(industry.icon as any, { className: "w-10 h-10 text-primary" })}
                </div>
                <h3 className="text-lg font-bold text-dark">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-primary font-bold tracking-wider text-sm mb-4 uppercase">
              Value Proposition
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-dark">
              Driving Real Business Impact
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "24/7 AI Automation",
                desc: "Automate customer support, lead handling, and operations — nonstop, error-free.",
                icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              },
              {
                title: "Multilingual AI (English + Regional)",
                desc: "Engage diverse Indian audiences naturally with multilingual AI experiences.",
                icon: "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
              },
              {
                title: "Revenue-Focused AI Solutions",
                desc: "AI that increases leads, conversions, and sales — not just dashboards.",
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              },
              {
                title: "Faster Response, Higher Conversions",
                desc: "Reply to customers in seconds, not hours — never miss a hot lead.",
                icon: "M13 10V3L4 14h7v7l9-11h-7z"
              },
              {
                title: "Reduce Costs, Scale Smart",
                desc: "Cut operational expenses by up to 30% without increasing manpower.",
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              },
              {
                title: "Data-Driven Decisions",
                desc: "Turn business data into actionable insights with predictive AI analytics.",
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              },
              {
                title: "Industry-Specific AI Models",
                desc: "Tailored AI for Real Estate, Hospitality, Retail, E-commerce & Enterprises.",
                icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              },
              {
                title: "Smart Lead Qualification",
                desc: "AI filters high-intent customers automatically for your sales team.",
                icon: "M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              },
              {
                title: "AI-Powered Creative Content",
                desc: "Generate premium ads, reels, visuals & videos at scale.",
                icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              },
              {
                title: "End-to-End AI Implementation",
                desc: "From strategy → development → deployment → optimization.",
                icon: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              }
            ].map((prop, index) => (
              <div
                key={index}
                className="group relative overflow-hidden bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col items-center text-center"
                onMouseEnter={() => setHoveredValueProp(index)}
                onMouseLeave={() => setHoveredValueProp(null)}
                style={{
                  backgroundImage: hoveredValueProp === index
                    ? `linear-gradient(to bottom right, #3b82f60d, transparent)`
                    : undefined
                }}
              >
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300"
                  style={{ boxShadow: `0 10px 15px -3px #3b82f633` }}>
                  <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={prop.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-dark mb-3 transition-colors duration-300"
                  style={{ color: hoveredValueProp === index ? '#3b82f6' : undefined }}>{prop.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* CTA Section */}
      {/* CTA Section */}
      <section className="py-14 bg-white border-t border-gray-100 mt-10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
            Ready to Scale in India & Beyond?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Partner with icubechat for AI solutions that understand your market and drive measurable growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-semibold"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/services"
              className="px-8 py-4 text-[#0B25E6] border-2 border-[#0B25E6] rounded-lg hover:bg-[#0B25E6] hover:text-white transition-colors text-lg font-semibold bg-transparent"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
